import React, { useState, useEffect } from "react";
import SEO from "../seo";

const AvailablApp = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
     const sections = [
  "overview",
  "google-sheets",
  "cod-partner",
  "cod-network",
  "space-seller",
  "cj-dropshipping"
];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Available Apps & Integrations | xPage Help Center"
        description="Discover available apps and integrations in xPage to automate workflows, sync products, manage orders, and streamline your e-commerce operations."
        keywords="xPage integrations, Google Sheets, COD, CJ Dropshipping, Space Seller, apps, automation"
        canonical="https://help.xpage.ai/help/apps/Available%20Apps"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">

        {/* Overview */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Available Apps & Integrations
          </h1>
          <p className="text-sm text-systemLabel">
            xPage provides a powerful set of free integrations designed to simplify your entire e-commerce workflow. From product sourcing and order automation to supplier management and COD fulfillment, these apps help you centralize operations in one place.
            You can easily connect your favorite services, sync data in real time, and automate key processes such as product imports, order forwarding, and delivery coordination — all without manual work.
          </p>
        </header>

{/* Google Sheets */}
<section id="google-sheets" className="mb-6 scroll-mt-20">
  <h2 className="text-sm font-semibold text-systemTitle mb-1">
    Google Sheets Integration
  </h2>

  <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
    <div className="space-y-4">

      <p className="text-sm text-systemLabel">
        Easily automate your order data by connecting xPage to Google Sheets.
      </p>

      {/* How It Works */}
      <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          How It Works
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
          <li>Install the Google Sheets app.</li>
          <li>
            Create an automation by selecting:
            <ul className="ml-4 list-disc list-inside space-y-1 mt-1">
              <li>Order type: Confirmed orders or abandoned checkouts</li>
              <li>Products: All products or specific ones</li>
              <li>Landing pages: All or selected pages</li>
            </ul>
          </li>
        </ul>

        <p className="text-systemLabel text-sm mt-2">
          This allows you to avoid unnecessary data transfer and keep your workflows clean.
        </p>
      </div>

      {/* Spreadsheet Setup */}
      <div className="p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Spreadsheet Setup Options
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
          <li>Select an existing spreadsheet using the Google picker</li>
          <li>Create a new spreadsheet (auto-named)</li>
          <li>Add a new sheet to an existing spreadsheet</li>
        </ul>
      </div>

      {/* Customizing */}
      <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Customizing Your Data
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
          <li>Choose from predefined column presets (e.g., basic orders)</li>
          <li>Fully customize columns:</li>
          <li className="ml-4">Reorder fields</li>
          <li className="ml-4">Rename columns</li>
          <li className="ml-4">Adjust data types</li>
          <li className="ml-4">Add or remove fields</li>
        </ul>
      </div>

      {/* Final Step */}
      <div className="p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Final Step
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
          <li>Your sheet is created automatically</li>
          <li>Existing orders are added instantly</li>
          <li>New orders sync in real time</li>
          <li>
            You can also create multiple sheets with different configurations to connect with suppliers and delivery partners simultaneously.
          </li>
        </ul>
      </div>

    </div>
    {/* Video Demo */}
<div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">

  {/* English Video */}
  <a
    href="https://youtu.be/lojVci60bI0?si=M6EVK_tQMSj1VOP_"
    target="_blank"
    rel="noopener noreferrer"
    className="group flex items-center justify-between p-3 rounded-lg border border-systemBorder bg-systemBackground hover:bg-gray-400/10 transition"
  >
    <div>
      <p className="text-sm font-medium text-systemTitle">
        🎥 Video Demo (English)
      </p>
      <p className="text-xs text-systemLabel">
        Watch how Google Sheets integration works
      </p>
    </div>

    <span className="text-xs text-systemLabel group-hover:text-systemTitle transition">
      Watch →
    </span>
  </a>

  {/* Arabic Video */}
  <a
    href="https://youtu.be/q6cEP0V4A40"
    target="_blank"
    rel="noopener noreferrer"
    className="group flex items-center justify-between p-3 rounded-lg border border-systemBorder bg-systemBackground hover:bg-gray-400/10 transition"
  >
    <div>
      <p className="text-sm font-medium text-systemTitle">
        🎥 Video Demo (Arabic)
      </p>
      <p className="text-xs text-systemLabel">
        شرح تكامل Google Sheets
      </p>
    </div>

    <span className="text-xs text-systemLabel group-hover:text-systemTitle transition">
      Watch →
    </span>
  </a>

</div>
  </div>

 
</section>


{/* COD Partner */}
<section id="cod-partner" className="mb-6 scroll-mt-20">
  <h2 className="text-sm font-semibold text-systemTitle mb-1">
    COD Partner Integration
  </h2>

  <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
    <div className="space-y-4">

      <p className="text-sm text-systemLabel">
        You can connect your Cash on Delivery (COD) partner directly to xPage for automatic order syncing and fulfillment.
      </p>

      {/* Setup */}
      <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Setup
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
          <li>Log in using your COD partner credentials</li>
          <li>Select the landing pages you want to link</li>
        </ul>
      </div>

      {/* Requirements */}
      <div className="p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Important Requirements
        </p>

        <p className="text-systemLabel text-sm mb-2">
          For orders to sync successfully, the following conditions must be met:
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-2">

          <li>
            <span className="font-medium">Product SKU Match</span>
            <ul className="ml-4 list-disc list-inside mt-1 space-y-1">
              <li>SKU must exactly match COD partner dashboard</li>
              <li>No typos, spaces, or case differences</li>
            </ul>
          </li>

          <li>
            <span className="font-medium">Valid Phone Number</span>
            <ul className="ml-4 list-disc list-inside mt-1 space-y-1">
              <li>Correct country format required</li>
              <li>Must include valid digit count</li>
            </ul>
          </li>

          <li>
            <span className="font-medium">Currency Match</span>
            <ul className="ml-4 list-disc list-inside mt-1 space-y-1">
              <li>Store currency must match target country</li>
              <li>Example: Saudi Arabia → SAR not USD</li>
            </ul>
          </li>

        </ul>
      </div>

      {/* Troubleshooting */}
      <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Troubleshooting
        </p>

        <p className="text-systemLabel text-sm">
          If orders are not syncing, check the “Common Issues” tab داخل التطبيق for detailed error explanations.
        </p>
      </div>

      {/* Result */}
      <div className="p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Result
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
          <li>Orders are automatically synced</li>
          <li>Sent directly to COD partner</li>
          <li>Ready for fulfillment without manual work</li>
        </ul>
      </div>

    </div>
  </div>
</section>
        {/* COD Network */}
        <section id="cod-network" className="mb-6 scroll-mt-20">
  <h2 className="text-sm font-semibold text-systemTitle mb-1">
    COD Network Integration
  </h2>

  <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
    <div className="space-y-4">

      <p className="text-sm text-systemLabel">
        You can connect your COD Network account directly to xPage to sync products and automate order fulfillment.
      </p>

      {/* Setup */}
      <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Setup
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
          <li>Log in to your COD Network account</li>
          <li>Navigate to Profile → API Key</li>
          <li>Generate a new API key</li>
          <li>Copy and paste the API key into the COD Network app inside xPage</li>
          <li>Once added, your accounts will be automatically connected.</li>
        </ul>
      </div>

      {/* Syncing Products */}
      <div className="p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Syncing Products
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
          <li>Go to the Products section in xPage</li>
          <li>Click “Sync from COD Network”</li>
          <li>Your products (including COD drop products) will be displayed</li>
          <li>Select one or multiple products to import</li>
          <li>After syncing:</li>
          <li className="ml-4">Products are created in xPage</li>
          <li className="ml-4">You can generate landing pages directly from them</li>
        </ul>
      </div>

      {/* Important Rules */}
      <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Important Rules
        </p>

        <p className="text-systemLabel text-sm mb-2">
          To ensure proper order syncing:
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-2">

          <li>
            <span className="font-medium">Do not modify the SKU</span>
            <ul className="ml-4 list-disc list-inside mt-1 space-y-1">
              <li>The SKU must remain exactly the same as imported from COD Network</li>
            </ul>
          </li>

          <li>
            <span className="font-medium">Adjust only allowed fields</span>
            <ul className="ml-4 list-disc list-inside mt-1 space-y-1">
              <li>You can edit content, design, and other product details — but not the SKU</li>
            </ul>
          </li>

          <li>
            <span className="font-medium">Set the correct currency</span>
            <ul className="ml-4 list-disc list-inside mt-1 space-y-1">
              <li>Make sure your store currency matches the target market</li>
            </ul>
          </li>

        </ul>
      </div>

      {/* Order Sync */}
      <div className="p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Order Sync
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
          <li>Orders generated from landing pages created using imported products will be automatically synced with COD Network</li>
        </ul>
      </div>

      {/* Important Note */}
      <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Important Note
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
          <li>Using an already existing product with a matching COD Network SKU is not sufficient.</li>
          <li>The product must be imported directly from COD Network via the app</li>
          <li>Only imported products are recognized for automatic order syncing</li>
        </ul>
      </div>

    </div>
  </div>
</section>

        {/* Space Seller */}
      <section id="space-seller" className="mb-6 scroll-mt-20">
  <h2 className="text-sm font-semibold text-systemTitle mb-1">
    Space Seller Integration
  </h2>

  <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
    <div className="space-y-4">

      <p className="text-sm text-systemLabel">
        Connect Space Seller with xPage to automatically send orders from your landing pages via webhook, ensuring accurate routing and real-time synchronization.
      </p>

      {/* Step 1 */}
      <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Step 1 — Create a Sales Channel in Space Seller
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
          <li>Each landing page requires its own dedicated sales channel in Space Seller.</li>
          <li>Do not reuse the same channel across multiple landing pages</li>
          <li>Each page must have a unique channel to avoid order misrouting</li>
          <li>How to Create It</li>
          <li>Go to Sales Channels from the Space Seller sidebar</li>
          <li>Open Integrations and select xPage</li>
          <li>
            Fill in the required details:
            <ul className="ml-4 list-disc list-inside space-y-1 mt-1">
              <li>Source Name: Internal label (e.g., Body Spray FR)</li>
              <li>Store Link: Full URL of your landing page</li>
            </ul>
          </li>
          <li>Create a separate sales channel for every landing page to ensure clean tracking and correct webhook usage.</li>
        </ul>
      </div>

      {/* Step 2 */}
      <div className="p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Step 2 — Map the Webhook in xPage
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
          <li>After creating the sales channel, you need to link it inside xPage.</li>
          <li>How to Map</li>
          <li>Open the Space Seller app in xPage</li>
          <li>Click Create Webhook Mapping</li>
          <li>Select the landing page</li>
          <li>Paste the Webhook URL from Space Seller</li>
          <li>Confirm mapping</li>
          <li>How to Find the Webhook URL</li>
          <li>Go to Space Seller → Sales Channels</li>
          <li>Open the channel you created</li>
          <li>Copy the Webhook URL</li>
          <li>Paste it into xPage</li>
          <li>Each landing page must have its own webhook mapping.</li>
          <li>Using the wrong webhook will send orders to the wrong channel.</li>
        </ul>
      </div>

      {/* Step 3 */}
      <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Step 3 — Order Transmission
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
          <li>Once the mapping is active:</li>
          <li>Orders from your landing page are automatically sent to Space Seller</li>
          <li>Transmission happens in real time via webhook</li>
          <li>No manual export is required</li>
        </ul>
      </div>

      {/* Order Data Requirements */}
      <div className="p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Order Data Requirements
        </p>

        <p className="text-systemLabel text-sm mb-2">
          To ensure successful order transmission, all required fields must be included.
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-2">

          <li>
            <span className="font-medium">Required Fields</span>
            <ul className="ml-4 list-disc list-inside space-y-1 mt-1">
              <li>price</li>
              <li>client_name</li>
              <li>client_phone</li>
              <li>client_address</li>
              <li>products</li>
            </ul>
          </li>

          <li>
            <span className="font-medium">Optional Fields</span>
            <ul className="ml-4 list-disc list-inside space-y-1 mt-1">
              <li>client_city</li>
              <li>note</li>
            </ul>
          </li>

        </ul>
      </div>

      {/* Product Data */}
      <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Product Data (Inside products Array)
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
          <li>Each product must include:</li>
          <li>product_name (required)</li>
          <li>sku (required)</li>
          <li>quantity (required)</li>
          <li>total (required)</li>
        </ul>
      </div>

      {/* Critical Rule */}
      <div className="p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Critical Rule — SKU Matching
        </p>

        <p className="text-systemLabel text-sm mb-2">
          The SKU is case-sensitive and must match exactly between xPage and Space Seller.
          Any mismatch (including uppercase/lowercase differences, spaces, or typos) will result in order rejection.
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-2">

          <li>
            <span className="font-medium">Example</span>
          </li>

          <li>
            Will Fail
            <ul className="ml-4 list-disc list-inside space-y-1 mt-1">
              <li>xPage SKU: bfs-50ml-oud</li>
              <li>Space Seller SKU: BFS-50ML-OUD</li>
              <li>→ Order rejected</li>
            </ul>
          </li>

          <li>
            Will Work
            <ul className="ml-4 list-disc list-inside space-y-1 mt-1">
              <li>xPage SKU: BFS-50ML-OUD</li>
              <li>Space Seller SKU: BFS-50ML-OUD</li>
              <li>→ Order created successfully</li>
            </ul>
          </li>

        </ul>
      </div>

      {/* Key Takeaways */}
      <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Key Takeaways
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
          <li>Use one sales channel per landing page</li>
          <li>Create one webhook mapping per landing page</li>
          <li>Always use the correct webhook URL</li>
          <li>Ensure exact SKU matching (case-sensitive)</li>
        </ul>
      </div>

    </div>
  </div>
</section>

        {/* CJ Dropshipping */}
     <section id="cj-dropshipping" className="mb-6 scroll-mt-20">
  <h2 className="text-sm font-semibold text-systemTitle mb-1">
    CJ Dropshipping Integration
  </h2>

  <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
    <div className="space-y-4">

      <p className="text-sm text-systemLabel">
        Connect your CJ Dropshipping account with xPage to import products and automatically sync orders for fulfillment.
      </p>

      {/* Step 1 */}
      <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Step 1 — Connect Your Account
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
          <li>Log in to your CJ Dropshipping account</li>
          <li>Navigate to Apps</li>
          <li>Open the API section</li>
          <li>Enter a name to generate your API key</li>
          <li>Copy the API key and paste it into the CJ Dropshipping app inside xPage</li>
          <li>Once added, your account will be connected automatically.</li>
        </ul>
      </div>

      {/* Step 2 */}
      <div className="p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Step 2 — Import Products
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
          <li>After connecting, your CJ products will appear inside the xPage app</li>
          <li>Select one or multiple products to import</li>
          <li>Imported products will be available in your xPage product dashboard</li>
          <li>You can then generate landing pages directly from these products.</li>
        </ul>
      </div>

      {/* Step 3 */}
      <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Step 3 — Order Sync
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
          <li>Orders generated from landing pages created using imported CJ products are automatically synced with CJ Dropshipping</li>
          <li>You can find synced orders inside:</li>
          <li>CJ Dropshipping → Orders → Store Orders</li>
        </ul>
      </div>

      {/* Important Notes */}
      <div className="p-3 rounded border border-systemBorder">
        <p className="text-systemTitle text-sm font-medium mb-1">
          Important Notes
        </p>

        <ul className="text-systemLabel text-sm list-disc list-inside space-y-1">
          <li>Only products imported via the CJ Dropshipping app will support automatic order syncing</li>
          <li>You can edit product content and landing page design, but keep core product structure intact for proper sync</li>
        </ul>
      </div>

    </div>
  </div>
</section>
      </div>

      {/* Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>

          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>

            <li>
              <a
                href="#google-sheets"
                className={`block py-1 border-l-2 pl-3 ${
                  activeSection === "google-sheets"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Google Sheets Integration
              </a>
            </li>

            <li>
              <a
                href="#cod-partner"
                className={`block py-1 border-l-2 pl-3 ${
                  activeSection === "cod-partner"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                COD Partner Integration
              </a>
            </li>

            <li>
              <a
                href="#cod-network"
                className={`block py-1 border-l-2 pl-3 ${
                  activeSection === "cod-network"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                COD Network Integration
              </a>
            </li>

            <li>
              <a
                href="#space-seller"
                className={`block py-1 border-l-2 pl-3 ${
                  activeSection === "space-seller"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Space Seller Integration
              </a>
            </li>

            <li>
              <a
                href="#cj-dropshipping"
                className={`block py-1 border-l-2 pl-3 ${
                  activeSection === "cj-dropshipping"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                CJ Dropshipping Integration
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AvailablApp;